import { useQuery } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";
import {
	type ContentFileDto,
	ContentType,
	MetaAdsAdFormat,
	MetaAdsCampaignObjective,
	MetaAdsConversionLocation,
	MetaAdsCTA,
} from "shared";
import { Api } from "../api.config";
import { useModal } from "../common/modal/Modal";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { ModalSelectMedia } from "../post/ModalSelectMedia";
import { useCampaignWizard } from "./CampaignWizardContext";
import { CreativeMediaPreview } from "./CreativeMediaPreview";

const CAROUSEL_MEDIA_HINT =
	"Carousel ads use images only. Videos are not supported — select 2 to 10 images.";

export const StepCreative: FC = () => {
	const { draft, updateDraft, nextStep, prevStep } = useCampaignWizard();
	const { open } = useModal();
	const [error, setError] = useState("");
	const [showUtm, setShowUtm] = useState(false);

	const isLeadsInstantForm =
		draft.objective === MetaAdsCampaignObjective.OUTCOME_LEADS &&
		draft.conversionLocation === MetaAdsConversionLocation.INSTANT_FORM;

	const noUrlNeeded =
		isLeadsInstantForm ||
		(draft.objective === MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT &&
			(draft.conversionLocation === MetaAdsConversionLocation.ON_POST ||
				draft.conversionLocation === MetaAdsConversionLocation.ON_PAGE)) ||
		(draft.objective === MetaAdsCampaignObjective.OUTCOME_SALES &&
			draft.conversionLocation === MetaAdsConversionLocation.CALLS);

	const needsDestinationUrl = !noUrlNeeded;

	const isCarousel = draft.adFormat === MetaAdsAdFormat.CAROUSEL;

	const { data: mediaFiles } = useQuery({
		queryKey: ["MEDIA_BATCH", ...draft.mediaFileIds],
		queryFn: () => Api.GetMediaBatch(draft.mediaFileIds).then((r) => r.data),
		enabled: draft.mediaFileIds.length > 0,
	});

	const carouselImages = useMemo(
		() => mediaFiles?.filter((f) => f.type === ContentType.IMAGE) ?? [],
		[mediaFiles],
	);
	const carouselVideoCount = useMemo(
		() => mediaFiles?.filter((f) => f.type === ContentType.VIDEO).length ?? 0,
		[mediaFiles],
	);

	const handleAdFormatChange = async (adFormat: MetaAdsAdFormat) => {
		if (
			adFormat === MetaAdsAdFormat.CAROUSEL &&
			draft.mediaFileIds.length > 0
		) {
			const files: ContentFileDto[] =
				mediaFiles ??
				(await Api.GetMediaBatch(draft.mediaFileIds).then((r) => r.data ?? []));
			const imageIds = files
				.filter((f) => f.type === ContentType.IMAGE)
				.map((f) => f.id);
			updateDraft({ adFormat, mediaFileIds: imageIds });
			return;
		}
		updateDraft({ adFormat });
	};

	const handleRemoveCarouselVideos = () => {
		updateDraft({ mediaFileIds: carouselImages.map((f) => f.id) });
	};

	const handleSelectMedia = () => {
		open(ModalSelectMedia, {
			onChange: (ids: number[]) => {
				updateDraft({ mediaFileIds: ids });
			},
			values: draft.mediaFileIds,
			multiple: isCarousel,
			acceptType: isCarousel ? ContentType.IMAGE : undefined,
			mediaHint: isCarousel ? CAROUSEL_MEDIA_HINT : undefined,
		});
	};

	const applyUtmSuggestions = (url: string) => {
		const updates: Partial<typeof draft> = { destinationUrl: url };
		try {
			const parsed = new URL(url);
			if (!draft.utmSource) updates.utmSource = "facebook";
			if (!draft.utmMedium) updates.utmMedium = "paid_social";
			if (!draft.utmCampaign) {
				updates.utmCampaign = draft.name.toLowerCase().replace(/\s+/g, "_");
			}
			if (!draft.displayLink) {
				updates.displayLink = parsed.hostname;
			}
		} catch {
			// Keep URL as typed.
		}
		updateDraft(updates);
	};

	const handleNext = () => {
		if (needsDestinationUrl && !draft.destinationUrl.trim()) {
			setError("Destination URL is required.");
			return;
		}
		if (!draft.primaryText.trim()) {
			setError("Primary text is required.");
			return;
		}
		if (isCarousel) {
			if (carouselImages.length < 2) {
				setError("Carousel ads require at least 2 images.");
				return;
			}
			if (carouselVideoCount > 0) {
				setError("Remove videos — carousel ads support images only.");
				return;
			}
		}
		setError("");
		nextStep();
	};

	return (
		<Card>
			<Card.Header>Ad Creative</Card.Header>
			<Card.Body>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					<div>
						<label className="form-label" htmlFor="ad-format">
							Ad Format
						</label>
						<Select
							id="ad-format"
							className="form-select"
							value={draft.adFormat}
							onChange={(e) =>
								handleAdFormatChange(e.target.value as MetaAdsAdFormat)
							}
						>
							<option value={MetaAdsAdFormat.SINGLE_IMAGE}>Single Image</option>
							<option value={MetaAdsAdFormat.SINGLE_VIDEO}>Single Video</option>
							<option value={MetaAdsAdFormat.CAROUSEL}>
								Carousel (images only)
							</option>
						</Select>
						{isCarousel && (
							// biome-ignore lint/a11y/useSemanticElements: Bootstrap alert is not form output
							<div className="alert alert-info py-2 mt-2 mb-0" role="status">
								<strong>Carousel uses images only.</strong> Select 2 to 10
								images. Videos cannot be used in carousel ads and will be
								removed if you switch from another format.
							</div>
						)}
					</div>

					<div>
						<label className="form-label" htmlFor="media-select-btn">
							Media
						</label>
						<div>
							<button
								id="media-select-btn"
								type="button"
								className="btn btn-outline-primary"
								onClick={handleSelectMedia}
							>
								{isCarousel
									? carouselImages.length > 0
										? `${carouselImages.length} image(s) selected`
										: "Select Images"
									: draft.mediaFileIds.length > 0
										? `${draft.mediaFileIds.length} file(s) selected`
										: "Select Media"}
							</button>
						</div>
						{isCarousel && carouselVideoCount > 0 && (
							<div
								className="alert alert-warning py-2 mt-2 mb-0"
								role="alert"
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 12,
									flexWrap: "wrap",
								}}
							>
								<span>
									<strong>{carouselVideoCount} video(s)</strong> in your
									selection cannot be used in a carousel. Remove them to
									continue.
								</span>
								<button
									type="button"
									className="btn btn-sm btn-warning"
									onClick={handleRemoveCarouselVideos}
								>
									Remove videos
								</button>
							</div>
						)}
						{isCarousel && carouselImages.length > 0 && (
							<div style={{ marginTop: 8, maxWidth: 400 }}>
								<CreativeMediaPreview
									files={carouselImages}
									layout="carousel"
									aspectRatio="1 / 1"
								/>
								{carouselImages.length === 1 && (
									<small className="text-muted">
										Add at least one more image for carousel.
									</small>
								)}
							</div>
						)}
						{!isCarousel && mediaFiles && mediaFiles.length > 0 && (
							<div style={{ marginTop: 8, maxWidth: 400 }}>
								<CreativeMediaPreview files={mediaFiles.slice(0, 1)} />
							</div>
						)}
					</div>

					<div>
						<label className="form-label" htmlFor="primary-text">
							Primary Text
						</label>
						<textarea
							id="primary-text"
							className="form-control"
							rows={3}
							value={draft.primaryText}
							onChange={(e) => updateDraft({ primaryText: e.target.value })}
							placeholder="The main text for your ad"
						/>
					</div>

					<div>
						<label className="form-label" htmlFor="headline">
							Headline
						</label>
						<input
							id="headline"
							type="text"
							className="form-control"
							value={draft.headline}
							onChange={(e) => updateDraft({ headline: e.target.value })}
							placeholder="Short headline"
						/>
					</div>

					<div>
						<label className="form-label" htmlFor="ad-description">
							Description
						</label>
						<input
							id="ad-description"
							type="text"
							className="form-control"
							value={draft.description}
							onChange={(e) => updateDraft({ description: e.target.value })}
							placeholder="Description text"
						/>
					</div>

					<div>
						<label className="form-label" htmlFor="display-link">
							Display Link
						</label>
						<input
							id="display-link"
							type="text"
							className="form-control"
							value={draft.displayLink}
							onChange={(e) => updateDraft({ displayLink: e.target.value })}
							placeholder="example.com"
						/>
					</div>

					<div>
						<label className="form-label" htmlFor="creative-cta">
							Call to Action
						</label>
						<Select
							id="creative-cta"
							className="form-select"
							value={draft.callToAction}
							onChange={(e) =>
								updateDraft({ callToAction: e.target.value as MetaAdsCTA })
							}
						>
							<option value={MetaAdsCTA.LEARN_MORE}>Learn More</option>
							{draft.objective === MetaAdsCampaignObjective.OUTCOME_SALES && (
								<>
									<option value={MetaAdsCTA.SHOP_NOW}>Shop Now</option>
									<option value={MetaAdsCTA.BUY_NOW}>Buy Now</option>
									<option value={MetaAdsCTA.ORDER_NOW}>Order Now</option>
									{draft.conversionLocation ===
										MetaAdsConversionLocation.CALLS && (
										<option value={MetaAdsCTA.CALL_NOW}>Call Now</option>
									)}
								</>
							)}
							{draft.objective ===
								MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT && (
								<>
									{(draft.conversionLocation ===
										MetaAdsConversionLocation.MESSENGER ||
										draft.conversionLocation ===
											MetaAdsConversionLocation.INSTAGRAM_DIRECT) && (
										<option value={MetaAdsCTA.SEND_MESSAGE}>
											Send Message
										</option>
									)}
									{draft.conversionLocation ===
										MetaAdsConversionLocation.ON_PAGE && (
										<option value={MetaAdsCTA.LIKE_PAGE}>Like Page</option>
									)}
									<option value={MetaAdsCTA.CONTACT_US}>Contact Us</option>
								</>
							)}
							{draft.objective !== MetaAdsCampaignObjective.OUTCOME_SALES &&
								draft.objective !==
									MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT && (
									<>
										<option value={MetaAdsCTA.SHOP_NOW}>Shop Now</option>
										<option value={MetaAdsCTA.SIGN_UP}>Sign Up</option>
										<option value={MetaAdsCTA.CONTACT_US}>Contact Us</option>
										<option value={MetaAdsCTA.GET_QUOTE}>Get Quote</option>
										<option value={MetaAdsCTA.BOOK_NOW}>Book Now</option>
									</>
								)}
						</Select>
					</div>

					{noUrlNeeded && (
						<div className="alert alert-info" style={{ marginBottom: 0 }}>
							{isLeadsInstantForm &&
								"Users will see your lead form when they click the ad. No destination URL is needed."}
							{draft.objective ===
								MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT &&
								draft.conversionLocation ===
									MetaAdsConversionLocation.ON_POST &&
								"Users will engage with your post directly (likes, comments, shares). No destination URL is needed."}
							{draft.objective ===
								MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT &&
								draft.conversionLocation ===
									MetaAdsConversionLocation.ON_PAGE &&
								"Users will be directed to like your page. No destination URL is needed."}
							{draft.objective === MetaAdsCampaignObjective.OUTCOME_SALES &&
								draft.conversionLocation === MetaAdsConversionLocation.CALLS &&
								"Users will see a Call Now button. No destination URL is needed."}
						</div>
					)}

					{needsDestinationUrl && (
						<>
							<div>
								<label className="form-label" htmlFor="destination-url">
									Destination URL
								</label>
								<input
									id="destination-url"
									type="url"
									className="form-control"
									value={draft.destinationUrl}
									onChange={(e) => applyUtmSuggestions(e.target.value)}
									onBlur={(e) => applyUtmSuggestions(e.target.value)}
									placeholder="https://example.com"
								/>
							</div>

							<div>
								<button
									type="button"
									className="btn btn-link p-0"
									onClick={() => setShowUtm(!showUtm)}
								>
									{showUtm ? "Hide" : "Show"} UTM Parameters
								</button>
							</div>

							{showUtm && (
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										gap: 12,
										paddingLeft: 16,
									}}
								>
									<div>
										<label className="form-label" htmlFor="utm-source">
											UTM Source
										</label>
										<input
											id="utm-source"
											type="text"
											className="form-control"
											value={draft.utmSource}
											onChange={(e) =>
												updateDraft({ utmSource: e.target.value })
											}
											placeholder="facebook"
										/>
									</div>
									<div>
										<label className="form-label" htmlFor="utm-medium">
											UTM Medium
										</label>
										<input
											id="utm-medium"
											type="text"
											className="form-control"
											value={draft.utmMedium}
											onChange={(e) =>
												updateDraft({ utmMedium: e.target.value })
											}
											placeholder="paid_social"
										/>
									</div>
									<div>
										<label className="form-label" htmlFor="utm-campaign">
											UTM Campaign
										</label>
										<input
											id="utm-campaign"
											type="text"
											className="form-control"
											value={draft.utmCampaign}
											onChange={(e) =>
												updateDraft({ utmCampaign: e.target.value })
											}
											placeholder="traffic_campaign"
										/>
									</div>
									<div>
										<label className="form-label" htmlFor="utm-content">
											UTM Content
										</label>
										<input
											id="utm-content"
											type="text"
											className="form-control"
											value={draft.utmContent}
											onChange={(e) =>
												updateDraft({ utmContent: e.target.value })
											}
										/>
									</div>
								</div>
							)}
						</>
					)}

					{error && <div className="text-danger">{error}</div>}

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<button
							type="button"
							className="btn btn-outline-secondary"
							onClick={prevStep}
						>
							Back
						</button>
						<button
							type="button"
							className="btn btn-primary"
							onClick={handleNext}
						>
							Next
						</button>
					</div>
				</div>
			</Card.Body>
		</Card>
	);
};
